import {
  FC,
  useState,
  useEffect,
  useRef,
  forwardRef,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
} from 'react';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { FAIcon, IconStyle, Tooltip, TooltipPreference, filterIconTheme } from '../';
import styles from './Accordion.module.scss';
import { AccordionPropsTypes, AccordionTheme } from './types/propsType';
import { ControlIconType, KeyConstants, FontSize } from './types/enums';

const clsBorderDesign = ({ isBorderDesign }: { isBorderDesign: boolean }): string => {
  return isBorderDesign ? '' : styles['no-border'];
};

const clsOpenState = ({ isAccordionOpen }: { isAccordionOpen: boolean }): string => {
  return isAccordionOpen ? `${styles['show']} ` : '';
};

const clsButtonState = ({ isAccordionOpen }: { isAccordionOpen: boolean }): string => {
  return !isAccordionOpen ? `${styles['button-radius-bottom']} ` : '';
};

const computeElement = ({ id, isAccordionOpen }: { id: string; isAccordionOpen: boolean }) => {
  const accordionElement = document.getElementById(id);
  const element =
    accordionElement?.querySelector('#nextgen-dropdown-component') ||
    accordionElement?.querySelector('#nextgen-datepicker-component');
  const accordionBody = document.getElementById(`${id}-collapse`);
  if (element && accordionBody) {
    accordionBody.style.overflow = isAccordionOpen ? 'initial' : 'hidden';
  }
};

const Accordion: FC<AccordionPropsTypes> = forwardRef<HTMLButtonElement, AccordionPropsTypes>(
  (props: AccordionPropsTypes, ref) => {
    const {
      id,
      className = '',
      contentTitle = '',
      content = '',
      title,
      iconType = ControlIconType.CHEVRON,
      children,
      childrenTitle,
      accordionInfo = false,
      accordionInfoText,
      onToggleCallback,
      isOpen: isPropAccordionOpen,
      fontSize = FontSize.LARGE,
      isBorderDesign = true,
      required = false,
      ...restProps
    } = props;

    const keys: string[] = [KeyConstants.ENTER, KeyConstants.SPACE];

    const [isAccordionOpen, setIsAccordionOpen] = useState(false);
    const [collapsibleHeight, setCollapsibleHeight] = useState('0px');
    const collapsibleRef = useRef<HTMLDivElement>(null);

    const getControlIcon = (isOpen = false): IconName => {
      if (iconType === ControlIconType.CHEVRON) {
        return 'chevron-down';
      } else if (isOpen) {
        return 'minus';
      } else {
        return 'plus';
      }
    };

    const handleEvent = (e: ReactKeyboardEvent | ReactMouseEvent) => {
      const isOpen = !isAccordionOpen;
      setIsAccordionOpen(isOpen);
      onToggleCallback?.(e, isOpen);
    };

    const shouldToggleOnKeyDown = (e: ReactKeyboardEvent) => {
      if (keys.includes(e.code)) {
        handleEvent(e);
      }
    };

    useEffect(() => {
      isPropAccordionOpen && setIsAccordionOpen(isPropAccordionOpen);
    }, [isPropAccordionOpen]);

    useEffect(() => {
      if (isAccordionOpen) {
        setTimeout(() => {
          if (collapsibleRef.current) {
            const height = Array.prototype.reduce.call(
              collapsibleRef.current.children,
              (acc, curr) => acc + curr.offsetHeight,
              0,
            );
            setCollapsibleHeight(`${height}px`);
            collapsibleRef.current.style.height = `${height}px`;
            collapsibleRef.current.style.overflow = 'initial'; // Allow content to be shown
          }
        }, 100);
      } else {
        if (collapsibleRef.current) {
          setCollapsibleHeight(`0px`);
          collapsibleRef.current.style.height = '0px';
          collapsibleRef.current.style.overflow = 'hidden'; // Hide content when collapsed
        }
      }
    }, [isAccordionOpen, children]);    

    useEffect(() => {
      computeElement({ id, isAccordionOpen });
    }, [isAccordionOpen]);

    const borderDesignCls = clsBorderDesign({ isBorderDesign });
    const containerCls = clsOpenState({ isAccordionOpen });
    const buttonBorderCls = clsButtonState({ isAccordionOpen });
    return (
      <div
        className={`${styles['accordion-item']} ${className} ${containerCls} ${borderDesignCls}`}
        id={id}
        role="region"
      >
        <h2 className={`${styles['accordion-header']}`} id={`${id}-accordion-header`}>
          <button
            id={`${id}-button`}
            className={`${styles['accordion-button']} ${buttonBorderCls} ${borderDesignCls}`}
            type="button"
            data-bs-toggle="collapse"
            data-bs-target={`${id}-collapse`}
            data-testid="accordion-test-id"
            aria-expanded={isAccordionOpen}
            aria-controls={`${id}-collapse`}
            ref={ref}
            onClick={handleEvent}
            onKeyDown={shouldToggleOnKeyDown}
            {...restProps}
          >
            <span className={styles['accordion-label-left']}>
              <label className={`${styles['accordion-label']} ${styles[fontSize]}`}>
                {title}
                {required ? <span className={styles['label-red']}>*</span> : null}
              </label>
              {accordionInfo && (
                <Tooltip
                  message={accordionInfoText || title}
                  preference={TooltipPreference.LEFT}
                  className={styles['icon-info']}
                />
              )}
              {childrenTitle}
            </span>
            <span>
              <FAIcon
                theme={filterIconTheme(restProps.theme)}
                iconName={getControlIcon(isAccordionOpen)}
                iconStyle={IconStyle.SOLID}
                className={`${styles['icon']} ${styles['icon-right']} `}
                data-testid="accordion-icon-testid"
              />
            </span>
          </button>
        </h2>
        <div
          id={`${id}-collapse`}
          className={`${styles['collapse']} ${containerCls} ${borderDesignCls}`}
          aria-label={`${id}-accordion-header`}
          data-bs-parent={`#${id}`}
          ref={collapsibleRef}
          style={{ height: collapsibleHeight }}
        >
          <div className={`${styles['accordion-body']}`}>
            {content && contentTitle ? (
              <>
                <div className={styles['content-title']}>{contentTitle}</div>
                <div>{content}</div>
              </>
            ) : (
              children
            )}
          </div>
        </div>
      </div>
    );
  },
);
Accordion.displayName = 'Accordion';
export default Accordion;
export { ControlIconType, FontSize };
export type { AccordionTheme };
